import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getJobProfiles, getFinalizedProfileTemplates } from '@/api/jobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useAppPagination } from '@/stores/app/pagination';
import { useFilters } from '@/stores/filters/filters';

export const useJobProfiles = defineStore('jobProfiles', () => {
  const { setLoading } = useAppLoading();

  const { selectedSubMenuOption } = storeToRefs(useSubMenu());

  const jobProfiles = ref([]);
  const sortOptions = ref([]);
  const finalizedTemplates = ref([]);

  const updateSortOptions = (newSortOptions) => {
    sortOptions.value = newSortOptions;
  };

  const loadJobProfiles = async ({
    mode,
    routeQuery,
    selectedFilters,
    page,
    searchTerm: newSearchTerm,
    selectedMyJobs,
  }) => {
    setLoading({ pageLoading: true, listLoading: true });

    try {
      let params = routeQuery || {};

      if (!params.sort && !selectedSubMenuOption.value) {
        params.sort = 'title';
      } else if (selectedSubMenuOption.value) {
        params = { ...params, sort: selectedSubMenuOption.value };
      }

      params.page = page || undefined;

      const parameters = {
        mode,
        params,
        selectedMyJobs,
        searchTerm: newSearchTerm,
        selectedFilters,
      };
      const newJobProfiles = await getJobProfiles(parameters);

      jobProfiles.value = newJobProfiles.documents;

      updateSortOptions((newJobProfiles?.sorting?.options || []).map(({ id }) => id));
      useAppPagination().updatePagination(newJobProfiles.pagination);

      if (newJobProfiles?.textFilter?.value) {
        useFilters().setSearchTerm(newJobProfiles.textFilter.value);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profiles load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false, listLoading: false });
    }
  };

  const loadFinalizedProfileTemplates = async () => {
    setLoading({ listLoading: true });
    try {
      const { templates } = await getFinalizedProfileTemplates();
      finalizedTemplates.value = templates;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Finalized templates load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  return {
    // State
    jobProfiles,
    sortOptions,
    finalizedTemplates,

    // Mutations
    updateSortOptions,

    // Actions
    loadJobProfiles,
    loadFinalizedProfileTemplates,
  };
});
